import { useState, useEffect, useRef } from "react";
import { api } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Trash2, Clock, Check, Volume2, Bell, Printer, XCircle } from "lucide-react";
import { toast } from "sonner";
import { playAlert, initAudio } from "@/lib/sound";

const STATUS_LABELS = {
  new: "Nouvelle", preparing: "En préparation", ready: "Prête",
  delivered: "Remise", completed: "Terminée", refused: "Refusée",
};
const NEXT_STATUS = { new: "preparing", preparing: "ready", ready: "delivered", delivered: "completed" };

export default function OrdersPanel() {
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState("all");
  const [soundOn, setSoundOn] = useState(() => localStorage.getItem("hana_sound_on") === "1");
  const prevNewIdsRef = useRef(new Set());
  const firstLoadRef = useRef(true);

  const load = async () => {
    try {
      const { data } = await api.get(`/admin/orders${filter !== "all" ? `?status=${filter}` : ""}`);
      setOrders(data);

      // detect new orders since last poll
      const currentNewIds = new Set(data.filter(o => o.status === "new").map(o => o.id));
      if (!firstLoadRef.current) {
        const fresh = [...currentNewIds].filter(id => !prevNewIdsRef.current.has(id));
        if (fresh.length > 0 && soundOn) {
          playAlert(3);
          toast.success(`🔔 ${fresh.length} nouvelle${fresh.length > 1 ? "s" : ""} commande${fresh.length > 1 ? "s" : ""} !`, { duration: 8000 });
          if (document.title.indexOf("(") === -1) document.title = `(${fresh.length}) ` + document.title;
        }
      }
      prevNewIdsRef.current = currentNewIds;
      firstLoadRef.current = false;
    } catch {
      toast.error("Impossible de charger les commandes");
    }
  };

  useEffect(() => { firstLoadRef.current = true; load(); const i = setInterval(load, 8000); return () => clearInterval(i); /* eslint-disable-next-line */ }, [filter, soundOn]);

  const enableSound = () => {
    initAudio();
    playAlert(1); // confirmation beep
    setSoundOn(true);
    localStorage.setItem("hana_sound_on", "1");
    toast.success("Son activé. Vous serez alerté pour chaque nouvelle commande.");
  };

  const disableSound = () => {
    setSoundOn(false);
    localStorage.setItem("hana_sound_on", "0");
  };

  const updateStatus = async (id, status) => {
    await api.put(`/admin/orders/${id}/status`, { status });
    toast.success("Statut mis à jour");
    load();
  };

  const del = async (id) => {
    if (!window.confirm("Supprimer cette commande ? (exclue de la compta)")) return;
    await api.delete(`/admin/orders/${id}`);
    toast.success("Commande supprimée");
    load();
  };

  const refuseOrder = async (id, orderNum) => {
    const reason = window.prompt(`Refuser la commande #${orderNum} ?\n\nRaison (envoyée au client par email, optionnel) :`);
    if (reason === null) return; // cancelled
    try {
      await api.post(`/admin/orders/${id}/refuse`, { reason: reason || "" });
      toast.success(`Commande #${orderNum} refusée · client notifié par email`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Impossible de refuser cette commande");
    }
  };

  const printOrder = async (id, orderNum) => {
    try {
      const { data } = await api.get(`/admin/orders/${id}/ticket-base64`);
      // Ouvre RawBT via URL scheme. Encodage en URL-safe base64.
      const encoded = encodeURIComponent(data.base64);
      window.location.href = `rawbt:base64,${encoded}`;
      toast.success(`Ticket #${orderNum} envoyé à RawBT`, { duration: 3000 });
    } catch (e) {
      toast.error("Impression échouée. RawBT est-il installé ?");
    }
  };

  const newCount = orders.filter(o => o.status === "new").length;

  return (
    <div data-testid="orders-panel">
      <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-display text-4xl text-foreground flex items-center gap-3">
            Commandes
            {newCount > 0 && <span className="text-base bg-primary text-white px-3 py-1 rounded-full animate-pulse">{newCount} nouvelle{newCount > 1 ? "s" : ""}</span>}
          </h1>
          <p className="text-muted-foreground text-sm mt-1">{orders.length} commande{orders.length > 1 ? "s" : ""} affichée{orders.length > 1 ? "s" : ""}</p>
        </div>
        {soundOn ? (
          <button onClick={disableSound} data-testid="disable-sound-btn" className="flex items-center gap-2 px-4 py-2 border border-border rounded-sm text-sm hover:bg-secondary">
            <Volume2 size={16} className="text-primary" /> Son activé · cliquez pour désactiver
          </button>
        ) : (
          <button onClick={enableSound} data-testid="enable-sound-btn" className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-sm text-sm hover:bg-primary/90 animate-pulse">
            <Bell size={16} /> Activer le son pour les nouvelles commandes
          </button>
        )}
      </div>

      <Tabs value={filter} onValueChange={setFilter} className="mb-6">
        <TabsList data-testid="orders-filter" className="flex flex-wrap h-auto gap-1 justify-start">
          <TabsTrigger value="all" data-testid="filter-all">Toutes</TabsTrigger>
          <TabsTrigger value="new" data-testid="filter-new">Nouvelles</TabsTrigger>
          <TabsTrigger value="preparing" data-testid="filter-preparing">En préparation</TabsTrigger>
          <TabsTrigger value="ready" data-testid="filter-ready">Prêtes</TabsTrigger>
          <TabsTrigger value="completed" data-testid="filter-completed">Terminées</TabsTrigger>
          <TabsTrigger value="refused" data-testid="filter-refused">Refusées</TabsTrigger>
        </TabsList>
      </Tabs>

      {orders.length === 0 ? (
        <div className="text-center py-20 text-muted-foreground">Aucune commande.</div>
      ) : (
        <div className="grid lg:grid-cols-2 gap-4">
          {orders.map((o) => (
            <div key={o.id} data-testid={`order-${o.order_number}`} className={`bg-white border rounded-sm p-5 transition ${o.status === "new" ? "border-primary ring-2 ring-primary/20" : "border-border"}`}>
              <div className="flex justify-between items-start mb-3 gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-3 flex-wrap">
                    <h3 className="font-display text-2xl">#{o.order_number}</h3>
                    <span className="text-xs uppercase tracking-wider px-2 py-1 bg-accent text-primary rounded-sm">{STATUS_LABELS[o.status]}</span>
                  </div>
                  <p className="mt-1 text-base font-semibold text-foreground">
                    {o.customer.firstname} {o.customer.lastname}
                  </p>
                  <a href={`tel:${o.customer.phone}`} className="text-base font-medium text-primary hover:underline block">
                    {o.customer.phone}
                  </a>
                  <p className="text-[11px] text-muted-foreground mt-1">
                    Reçue le {new Date(o.created_at).toLocaleString("fr-CH")}
                  </p>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-2xl font-semibold text-primary">{o.total.toFixed(2)} CHF</div>
                  {o.payment_method === "onsite" && (
                    <div className="mt-1 inline-block text-[10px] uppercase tracking-wider px-2 py-1 bg-amber-100 text-amber-900 rounded-sm font-semibold">
                      À payer sur place
                    </div>
                  )}
                </div>
              </div>

              {/* Heure demandée — bien en évidence */}
              <div className="mb-3 p-3 bg-primary/10 border-l-4 border-primary rounded-sm flex items-center gap-3">
                <Clock size={20} className="text-primary shrink-0" />
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-primary font-semibold">Heure de retrait demandée</div>
                  <div className="text-xl font-bold text-primary" data-testid={`pickup-time-${o.order_number}`}>
                    {o.pickup_time === "asap" ? "Dès que possible" : o.pickup_time}
                  </div>
                </div>
              </div>

              <div className="border-t border-border pt-3 space-y-1 text-sm">
                {o.items.map((it, i) => (
                  <div key={i}>
                    <span className="font-medium">{it.quantity}× {it.name}</span>
                    {it.addons?.length > 0 && <span className="text-muted-foreground"> + {it.addons.map(a => a.name).join(", ")}</span>}
                    {it.comment && <div className="text-xs italic text-muted-foreground ml-4">« {it.comment} »</div>}
                  </div>
                ))}
              </div>

              <div className="flex flex-wrap gap-2 mt-4 pt-3 border-t border-border">
                <button
                  onClick={() => printOrder(o.id, o.order_number)}
                  data-testid={`print-${o.order_number}`}
                  className="flex items-center gap-2 px-3 py-2 border border-primary text-primary rounded-sm text-sm hover:bg-primary hover:text-white transition font-medium"
                >
                  <Printer size={14} /> Imprimer
                </button>
                {NEXT_STATUS[o.status] && (
                  <button
                    onClick={() => updateStatus(o.id, NEXT_STATUS[o.status])}
                    data-testid={`advance-${o.order_number}`}
                    className="flex-1 min-w-[140px] bg-primary text-white py-2 rounded-sm text-sm hover:bg-primary/90 flex items-center justify-center gap-2"
                  >
                    <Check size={14} /> Passer à &quot;{STATUS_LABELS[NEXT_STATUS[o.status]]}&quot;
                  </button>
                )}
                {(o.status === "new" || o.status === "preparing") && (
                  <button
                    onClick={() => refuseOrder(o.id, o.order_number)}
                    data-testid={`refuse-${o.order_number}`}
                    className="flex items-center gap-2 px-3 py-2 border border-destructive text-destructive rounded-sm text-sm hover:bg-destructive hover:text-white transition"
                  >
                    <XCircle size={14} /> Refuser
                  </button>
                )}
                {(o.status === "completed" || o.status === "refused") && (
                  <button
                    onClick={() => del(o.id)}
                    data-testid={`delete-${o.order_number}`}
                    className="px-4 border border-destructive text-destructive py-2 rounded-sm text-sm hover:bg-destructive hover:text-white"
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
